// @ts-nocheck
import React, { useState } from 'react';
import { usePendingFeedback } from '@/hooks/api-hooks';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { format } from "date-fns";
import {
  ArrowLeft,
  Clock,
  AlertTriangle,
  User,
  MessageSquare,
  Calendar,
  ChevronRight,
  Zap,
  Star
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/auth-context";

export function FeedbackPendingPage() {
  // Fetch data from API
  const { data: pendingData, isLoading, error } = usePendingFeedback();
  const { user } = useAuth();

  const pendingFeedbackItems = pendingData?.data || [];

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading your pending feedback...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-destructive">Failed to load pending feedback. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  const overdueFeedback = pendingFeedbackItems.filter(item => item.isOverdue);
  const upcomingFeedback = pendingFeedbackItems.filter(item => !item.isOverdue);

  const generateFeedbackUrl = (cycleId: string, userId: string) => {
    // Mock feedback URL generator - replace with actual implementation
    return `/feedback/give/${cycleId}?user=${userId}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/feedback"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Feedback
          </Link>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl flex items-center justify-center">
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Pending Feedback</h1>
              <p className="text-muted-foreground text-lg">
                Feedback requests that need your attention
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 px-3 py-1 text-sm font-medium">
              {pendingFeedbackItems.length} Pending
            </Badge>
            {overdueFeedback.length > 0 && (
              <Badge className="bg-destructive/10 text-destructive border-destructive/20 px-3 py-1 text-sm font-medium">
                {overdueFeedback.length} Overdue
              </Badge>
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl flex items-center justify-center">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">{pendingFeedbackItems.length}</div>
                  <div className="text-sm text-muted-foreground">Total Pending</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-red-50 to-pink-50 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">{overdueFeedback.length}</div>
                  <div className="text-sm text-muted-foreground">Overdue</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl flex items-center justify-center">
                  <User className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {new Set(pendingFeedbackItems.map(item => item.target.id)).size}
                  </div>
                  <div className="text-sm text-muted-foreground">Recipients</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions for Overdue */}
        {overdueFeedback.length > 0 && (
          <Card className="mb-8 border-destructive/20 bg-destructive/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Overdue Feedback ({overdueFeedback.length})
              </CardTitle>
              <CardDescription className="text-destructive/80">
                These feedback requests are past their due date and need immediate attention.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {overdueFeedback.slice(0, 3).map((item) => {
                  const feedbackUrl = generateFeedbackUrl(item.cycle.id, user?.id || '');

                  return (
                    <Link
                      key={item.id}
                      to={feedbackUrl}
                      className="flex items-center justify-between p-3 bg-background rounded-lg border border-destructive/20 hover:border-destructive/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={item.target.avatar} />
                          <AvatarFallback className={item.target.avatarColor}>
                            {item.target.initials}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-foreground">{item.cycle.name}</div>
                          <div className="text-sm text-muted-foreground">For {item.target.name} • {item.daysPastDue} days overdue</div>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                  );
                })}
                {overdueFeedback.length > 3 && (
                  <div className="text-sm text-destructive text-center pt-2">
                    +{overdueFeedback.length - 3} more overdue items below
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {pendingFeedbackItems.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Star className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">All caught up!</h3>
              <p className="text-muted-foreground mb-6">You don't have any pending feedback to provide right now.</p>
              <Link to="/feedback">
                <Button className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Feedback Dashboard
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Pending Feedback List */}
        {pendingFeedbackItems.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Your Pending Feedback</CardTitle>
              <CardDescription>
                Click "Start Feedback" to begin providing feedback for each request
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pendingFeedbackItems.map((item) => {
                  const feedbackUrl = generateFeedbackUrl(item.cycle.id, user?.id || '');

                  return (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-border/80 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        {/* Feedback Info */}
                        <div className="flex-1">
                          <div className="space-y-1">
                            <div className="font-medium text-foreground">{item.cycle.name}</div>
                            <div className="text-sm text-muted-foreground">{item.cycle.description || item.cycle.type}</div>
                            <div className="flex items-center gap-2">
                              <Badge className={cn(
                                "text-xs",
                                item.cycle.type === 'Performance Review' ? "bg-purple-100 text-purple-800 border-purple-200" :
                                item.cycle.type === '360 Review' ? "bg-blue-100 text-blue-800 border-blue-200" :
                                "bg-muted text-foreground border-border"
                              )}>
                                {item.cycle.type}
                              </Badge>
                              <Badge className={cn(
                                "text-xs",
                                item.priority === 'high' ? "bg-destructive/10 text-destructive border-destructive/20" :
                                item.priority === 'medium' ? "bg-yellow-100 text-yellow-800 border-yellow-200" :
                                "bg-muted text-foreground border-border"
                              )}>
                                {item.priority} priority
                              </Badge>
                              {item.isOverdue && (
                                <Badge className="bg-destructive/10 text-destructive border-destructive/20 text-xs">
                                  Overdue
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Target User */}
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={item.target.avatar} />
                            <AvatarFallback className={item.target.avatarColor}>
                              {item.target.initials}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium text-foreground">{item.target.name}</div>
                            <div className="text-sm text-muted-foreground">{item.target.role}</div>
                          </div>
                        </div>

                        {/* Timeline */}
                        <div className="text-right">
                          <div className="font-medium text-foreground">
                            Due: {format(new Date(item.dueDate), "MMM d, yyyy")}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Requested: {format(new Date(item.invitedAt), "MMM d, yyyy")}
                          </div>
                          {item.isOverdue && (
                            <div className="text-sm text-destructive font-medium mt-1">
                              {item.daysPastDue} days overdue
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 ml-4">
                        <Link to={feedbackUrl}>
                          <Button
                            size="sm"
                            className={cn(
                              "gap-2 text-xs",
                              item.isOverdue
                                ? "bg-destructive hover:bg-destructive/90"
                                : "bg-primary hover:bg-primary/90"
                            )}
                          >
                            <MessageSquare className="h-3 w-3" />
                            Start Feedback
                          </Button>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default FeedbackPendingPage
